// src/pages/CreatePost.js
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useStore } from '../store';
import { groupsAPI, mediaAPI, postsAPI } from '../utils/api';
import { getPlatform, PLATFORM_CHAR_LIMITS } from '../utils/platforms';

const EMOJIS = ['😀','😁','😂','🤣','😊','😍','🤩','😎','🥳','🤔','👍','👏','🙌','🔥','💡','✨','🎉','🚀','💯','❤️','✅','📈','📣','⭐','🙏','💪','🎯','📌','👀','😢','😮','🤝'];

// Uniform line-icon set for the preview engagement bar — one stroke weight/size/color
// for every platform, so the row reads as one consistent style (only the glyph and
// the labeled-vs-icons-only layout differ), matching how SocialPilot's preview looks.
const IconBase = ({ children, size = 15, color = '#8892a4', style }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ display: 'block', flexShrink: 0, ...style }}>
    {children}
  </svg>
);
const IconThumbUp   = (p) => <IconBase {...p}><path d="M14 9V5a3 3 0 0 0-3-3l-4 9v11h11.28a2 2 0 0 0 2-1.7l1.38-9a2 2 0 0 0-2-2.3z" /><path d="M7 22H4a2 2 0 0 1-2-2v-7a2 2 0 0 1 2-2h3" /></IconBase>;
const IconThumbDown = (p) => <IconBase {...p}><path d="M10 15v4a3 3 0 0 0 3 3l4-9V2H5.72a2 2 0 0 0-2 1.7l-1.38 9a2 2 0 0 0 2 2.3z" /><path d="M17 2h3a2 2 0 0 1 2 2v7a2 2 0 0 1-2 2h-3" /></IconBase>;
const IconComment   = (p) => <IconBase {...p}><path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" /></IconBase>;
const IconSend      = (p) => <IconBase {...p}><line x1="22" y1="2" x2="11" y2="13" /><polygon points="22 2 15 22 11 13 2 9 22 2" /></IconBase>;
const IconRepeat    = (p) => <IconBase {...p}><polyline points="17 1 21 5 17 9" /><path d="M3 11V9a4 4 0 0 1 4-4h14" /><polyline points="7 23 3 19 7 15" /><path d="M21 13v2a4 4 0 0 1-4 4H3" /></IconBase>;
const IconHeart     = (p) => <IconBase {...p}><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" /></IconBase>;
const IconShare     = (p) => <IconBase {...p}><circle cx="18" cy="5" r="3" /><circle cx="6" cy="12" r="3" /><circle cx="18" cy="19" r="3" /><line x1="8.59" y1="13.51" x2="15.42" y2="17.49" /><line x1="15.41" y1="6.51" x2="8.59" y2="10.49" /></IconBase>;
const IconBookmark  = (p) => <IconBase {...p}><path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z" /></IconBase>;
const IconExternal  = (p) => <IconBase {...p}><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" /><polyline points="15 3 21 3 21 9" /><line x1="10" y1="14" x2="21" y2="3" /></IconBase>;

// Each platform's own engagement-bar layout in the preview, mirroring what that
// network's real post actions look like (SocialPilot does the same per-platform).
const PREVIEW_ACTIONS = {
  facebook:  { style: 'labeled', items: [{ Icon: IconThumbUp, label: 'Like' }, { Icon: IconComment, label: 'Comment' }, { Icon: IconSend, label: 'Send' }] },
  linkedin:  { style: 'labeled', items: [{ Icon: IconThumbUp, label: 'Like' }, { Icon: IconComment, label: 'Comment' }, { Icon: IconRepeat, label: 'Repost' }, { Icon: IconSend, label: 'Send' }] },
  gmb:       { style: 'share-only', Icon: IconExternal },
  pinterest: { style: 'share-only', Icon: IconExternal },
  x:         { style: 'icons', items: [IconComment, IconRepeat, IconHeart, IconShare] },
  instagram: { style: 'icons', items: [IconHeart, IconComment, IconSend], trailing: IconBookmark },
  tiktok:    { style: 'icons', items: [IconHeart, IconComment, IconSend] },
  youtube:   { style: 'labeled', items: [{ Icon: IconThumbUp, label: 'Like' }, { Icon: IconThumbDown, label: 'Dislike' }, { Icon: IconShare, label: 'Share' }] },
};
const getPreviewActions = (platform) => PREVIEW_ACTIONS[platform] || { style: 'icons', items: [IconHeart, IconComment, IconSend] };

export default function CreatePost() {
  const navigate = useNavigate();
  const { postId } = useParams(); // present when editing
  const isEdit = Boolean(postId);
  const { accounts, fetchAccounts, groups, fetchGroups, createPost, fetchSummary } = useStore();
  const [caption, setCaption] = useState('');
  const [selectedAccounts, setSelectedAccounts] = useState([]);
  const [selectedGroupIds, setSelectedGroupIds] = useState([]);
  const [groupAccountsMap, setGroupAccountsMap] = useState({}); // groupId -> [account_id, ...]
  const [excludedAccountIds, setExcludedAccountIds] = useState([]); // accounts unchecked out of a selected group
  const [scheduledAt, setScheduledAt] = useState('');
  const [scheduledTime, setScheduledTime] = useState('09:00');
  const [mediaFiles, setMediaFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [videoMeta, setVideoMeta] = useState({}); // { [fileId]: { title, thumbnailUrl, subtitleName } }
  const [videoDurations, setVideoDurations] = useState({}); // { [fileId]: seconds }
  const thumbInputRef = useRef();
  const subInputRef = useRef();
  const [activeVideoId, setActiveVideoId] = useState(null);
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [showGroupDropdown, setShowGroupDropdown] = useState(false);
  const [customizeOn, setCustomizeOn] = useState(false);
  const [customCaptions, setCustomCaptions] = useState({});
  const [activeCustomTab, setActiveCustomTab] = useState(null);
  const [accSearch, setAccSearch] = useState('');
  const [groupSearch, setGroupSearch] = useState('');
  const [accTab, setAccTab] = useState('accounts'); // 'accounts' | 'groups'
  const [autosave, setAutosave] = useState('Autosave');
  const [loadingPost, setLoadingPost] = useState(isEdit);
  const [showPostDropdown, setShowPostDropdown] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [showEmoji, setShowEmoji] = useState(false);
  const [showGallery, setShowGallery] = useState(false);
  const [library, setLibrary] = useState(null); // null = loading
  const fileInputRef = useRef();
  const captionRef = useRef();
  const groupDropRef = useRef();
  const postDropRef = useRef();

  useEffect(() => {
    fetchAccounts();
    fetchGroups();
    if (isEdit) loadPostForEdit();
  }, []);

  const loadPostForEdit = async () => {
    setLoadingPost(true);
    try {
      const res = await postsAPI.get(postId);
      const p = res.data.post;
      setCaption(p.caption || '');
      if (p.scheduled_at) {
        const dt = new Date(p.scheduled_at);
        setScheduledAt(dt.toISOString().slice(0, 10));
        setScheduledTime(dt.toTimeString().slice(0, 5));
      }
      // Pre-select accounts from post_accounts
      const accIds = (p.accounts || []).filter(a => a && a.account_id).map(a => a.account_id);
      setSelectedAccounts(accIds);
      // Pre-load media files if any
      if (p.media_ids?.length) {
        const mediaRes = await mediaAPI.list();
        const relevant = mediaRes.data.files.filter(f => p.media_ids.includes(f.id));
        setMediaFiles(relevant);
      }
    } catch {
      toast.error('Failed to load post');
      navigate('/posts/manage');
    } finally {
      setLoadingPost(false);
    }
  };

  // Close group dropdown on outside click
  useEffect(() => {
    const handler = (e) => {
      if (groupDropRef.current && !groupDropRef.current.contains(e.target)) setShowGroupDropdown(false);
      if (postDropRef.current && !postDropRef.current.contains(e.target)) setShowPostDropdown(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Autosave indicator
  useEffect(() => {
    if (!caption) return;
    setAutosave('Saving…');
    const t = setTimeout(() => setAutosave('Saved to Drafts'), 1200);
    return () => clearTimeout(t);
  }, [caption]);

  const filteredAccounts = accounts.filter(a => a.account_name.toLowerCase().includes(accSearch.toLowerCase()));
  const filteredGroups = groups.filter(g => g.name.toLowerCase().includes(groupSearch.toLowerCase()));

  // Group membership isn't included in the /groups list response (only a count is),
  // so fetch the member account ids on demand and cache them, the same way SocialPilot
  // resolves a group into its accounts as soon as it's selected.
  const ensureGroupAccounts = async (groupId) => {
    if (groupAccountsMap[groupId]) return groupAccountsMap[groupId];
    try {
      const res = await groupsAPI.get(groupId);
      const ids = (res.data.accounts || []).map(a => a.id);
      setGroupAccountsMap(prev => ({ ...prev, [groupId]: ids }));
      return ids;
    } catch {
      toast.error('Failed to load group accounts');
      return [];
    }
  };

  const toggleGroupSelection = async (groupId) => {
    if (selectedGroupIds.includes(groupId)) {
      setSelectedGroupIds(prev => prev.filter(x => x !== groupId));
      // Exclusions were only scoped to seeing this account via this group — clear them
      // unless the account is still pulled in by another currently selected group.
      const removed = groupAccountsMap[groupId] || [];
      setExcludedAccountIds(prev => prev.filter(id => {
        if (!removed.includes(id)) return true;
        return selectedGroupIds.some(gid => gid !== groupId && (groupAccountsMap[gid] || []).includes(id));
      }));
    } else {
      setSelectedGroupIds(prev => [...prev, groupId]);
      await ensureGroupAccounts(groupId);
    }
  };

  // Accounts selected directly in the "Clients" list, plus every account that belongs
  // to a currently selected group and hasn't been individually unchecked back out —
  // this is what should actually show as checked/previewed.
  const groupSelectedAccountIds = new Set(selectedGroupIds.flatMap(gid => groupAccountsMap[gid] || []));
  const effectiveAccountIds = new Set([
    ...selectedAccounts,
    ...[...groupSelectedAccountIds].filter(id => !excludedAccountIds.includes(id)),
  ]);

  const toggleAccount = (id) => {
    const isManual = selectedAccounts.includes(id);
    const isViaGroup = groupSelectedAccountIds.has(id) && !isManual;
    const isExcluded = excludedAccountIds.includes(id);

    if (isManual) {
      setSelectedAccounts(prev => prev.filter(x => x !== id));
    } else if (isViaGroup && !isExcluded) {
      // Checked because a selected group includes it — uncheck it just for this account.
      setExcludedAccountIds(prev => [...prev, id]);
    } else if (isExcluded) {
      setExcludedAccountIds(prev => prev.filter(x => x !== id));
      if (!groupSelectedAccountIds.has(id)) setSelectedAccounts(prev => [...prev, id]);
    } else {
      setSelectedAccounts(prev => [...prev, id]);
    }
  };

  const selectAll = (checked) => {
    if (checked) {
      setSelectedAccounts(accounts.map(a => a.id));
      setExcludedAccountIds([]);
    } else {
      setSelectedAccounts([]);
      setSelectedGroupIds([]);
      setExcludedAccountIds([]);
    }
  };

  const insertAtCursor = (text) => {
    const el = captionRef.current;
    const editingCustom = customizeOn && activeCustomTab;
    const current = editingCustom ? (customCaptions[activeCustomTab] ?? caption) : caption;
    const start = el ? el.selectionStart : current.length;
    const end = el ? el.selectionEnd : current.length;
    const next = current.slice(0, start) + text + current.slice(end);
    if (editingCustom) setCustomCaptions(prev => ({ ...prev, [activeCustomTab]: next }));
    else setCaption(next);
    const pos = start + text.length;
    requestAnimationFrame(() => { if (el) { el.focus(); el.setSelectionRange(pos, pos); } });
  };

  const openGallery = async () => {
    setShowGallery(true);
    setLibrary(null);
    try {
      const res = await mediaAPI.list();
      setLibrary(res.data.files || []);
    } catch {
      setLibrary([]);
      toast.error('Could not load media library');
    }
  };

  const addFromGallery = (file) => {
    setMediaFiles(prev => prev.some(f => f.id === file.id) ? prev : [...prev, file]);
    setShowGallery(false);
  };

  const handleFileUpload = async (files) => {
    if (!files.length) return;
    setUploading(true);
    try {
      const formData = new FormData();
      Array.from(files).forEach(f => formData.append('files', f));
      const res = await mediaAPI.upload(formData);
      const uploaded = res.data.files;
      setMediaFiles(prev => [...prev, ...uploaded]);
      // Auto-set title from filename for first video
      uploaded.forEach(f => {
        if (f.file_type?.startsWith('video/')) {
          setActiveVideoId(f.id);
          setVideoMeta(prev => ({
            ...prev,
            [f.id]: { title: f.original_name?.replace(/\.[^.]+$/, '') || '', thumbnailUrl: null, subtitleName: '' },
          }));
        }
      });
      toast.success(`${uploaded.length} file(s) uploaded`);
    } catch {
      toast.error('Upload failed');
    } finally { setUploading(false); }
  };

  const removeMedia = (id) => {
    setMediaFiles(prev => prev.filter(x => x.id !== id));
    setVideoMeta(prev => { const n = { ...prev }; delete n[id]; return n; });
    setVideoDurations(prev => { const n = { ...prev }; delete n[id]; return n; });
    if (activeVideoId === id) setActiveVideoId(null);
  };

  const handleThumbnailChange = (fileId, e) => {
    const file = e.target.files[0];
    if (!file) return;
    const url = URL.createObjectURL(file);
    setVideoMeta(prev => ({ ...prev, [fileId]: { ...prev[fileId], thumbnailUrl: url } }));
  };

  const handleSubtitleChange = (fileId, e) => {
    const file = e.target.files[0];
    if (!file) return;
    setVideoMeta(prev => ({ ...prev, [fileId]: { ...prev[fileId], subtitleName: file.name } }));
    toast.success(`Subtitle "${file.name}" attached`);
  };

  const formatDuration = (secs) => {
    if (!secs || isNaN(secs)) return '0:00';
    const m = Math.floor(secs / 60);
    const s = Math.floor(secs % 60);
    return `${m}:${s.toString().padStart(2, '0')}`;
  };

  const openScheduleModal = () => {
    if (!caption.trim()) { toast.error('Please write a caption'); return; }
    if (!selectedAccounts.length && !selectedGroupIds.length) { toast.error('Select at least one account or group'); return; }
    const tomorrow = new Date(); tomorrow.setDate(tomorrow.getDate() + 1);
    setScheduledAt(tomorrow.toISOString().slice(0, 10));
    setShowScheduleModal(true);
  };

  const postPayload = (extra = {}) => ({
    caption,
    account_ids: selectedAccounts,
    group_ids: selectedGroupIds,
    media_ids: mediaFiles.map(f => f.id),
    customizations: customizeOn ? customCaptions : {},
    ...extra,
  });

  const confirmSchedule = async () => {
    try {
      const dt = new Date(`${scheduledAt}T${scheduledTime}:00`);
      const payload = postPayload({ scheduled_at: dt.toISOString(), status: 'queued' });
      if (isEdit) {
        await postsAPI.update(postId, payload);
        toast.success('Post updated!');
      } else {
        await createPost(payload);
        toast.success('Post scheduled successfully!');
      }
      fetchSummary();
      setShowScheduleModal(false);
      navigate('/posts/manage');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to schedule post');
    }
  };

  const publishNow = async () => {
    if (!caption.trim()) { toast.error('Please write a caption first'); return; }
    if (!selectedAccounts.length && !selectedGroupIds.length) { toast.error('Select at least one account or group'); return; }
    setPublishing(true);
    setShowPostDropdown(false);
    try {
      // Set scheduled_at to now so the scheduler picks it up in the next run
      const payload = postPayload({ scheduled_at: new Date().toISOString(), status: 'queued' });
      if (isEdit) {
        await postsAPI.update(postId, payload);
      } else {
        await createPost(payload);
      }
      fetchSummary();
      toast.success('Post queued — publishing within 60 seconds');
      navigate('/posts/manage');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to queue post');
      setPublishing(false);
    }
  };

  const saveDraft = async () => {
    if (!caption.trim()) { toast.error('Write a caption first'); return; }
    try {
      const payload = postPayload({ status: 'draft' });
      if (isEdit) {
        await postsAPI.update(postId, payload);
        toast.success('Draft updated!');
      } else {
        await createPost(payload);
        toast.success('Draft saved!');
      }
      navigate('/posts/manage');
    } catch { toast.error('Failed to save draft'); }
  };

  const selectedPlatforms = [...new Set(accounts.filter(a => effectiveAccountIds.has(a.id)).map(a => a.platform))];
  const hasYouTubeSelected = selectedPlatforms.includes('youtube');
  const hasVideo = mediaFiles.some(f => f.file_type?.startsWith('video/'));
  const hasImage = mediaFiles.some(f => f.file_type?.startsWith('image/'));
  const hasLinkedInPage = accounts.some(a => effectiveAccountIds.has(a.id) && a.platform === 'linkedin' && /^\d+$/.test(a.account_id_external || ''));
  // Google Business Profile's API only accepts a photo (PHOTO mediaFormat) — the scheduler's
  // publishToGmb() only ever looks for an image file, so any attached video is silently
  // dropped at publish time unless we warn about it here first.
  const hasGmbSelected = selectedPlatforms.includes('gmb');
  const gmbVideoWarning = hasGmbSelected && hasVideo;

  // Tightest character limit across whatever is currently being edited (a single
  // platform tab under Customize, or all selected platforms when editing the shared caption).
  // On the "All" tab, a platform that already has its own customized caption is excluded —
  // it no longer uses this shared text, so its limit is checked on its own tab instead.
  const editingText = customizeOn && activeCustomTab ? (customCaptions[activeCustomTab] ?? caption) : caption;
  const limitPlatforms = customizeOn && activeCustomTab
    ? [activeCustomTab]
    : selectedPlatforms.filter(p => !(customizeOn && customCaptions[p] !== undefined));
  const tightestPlatformId = limitPlatforms.reduce((min, p) => {
    if (!(p in PLATFORM_CHAR_LIMITS)) return min;
    if (!min || PLATFORM_CHAR_LIMITS[p] < PLATFORM_CHAR_LIMITS[min]) return p;
    return min;
  }, null);
  const tightestLimit = tightestPlatformId ? PLATFORM_CHAR_LIMITS[tightestPlatformId] : null;
  const isOverLimit = tightestLimit != null && editingText.length > tightestLimit;
  const counterColor = tightestLimit == null ? '#aaa' : (isOverLimit ? '#dc2626' : '#16a34a');

  if (loadingPost) {
    return <div className="loading-center" style={{ height: '100%' }}><div className="spinner" /></div>;
  }

  return (
    <div style={{ display: 'flex', height: '100%', overflow: 'hidden' }}>
      {/* === ACCOUNT SIDEBAR === */}
      <div style={{ width: 200, background: '#fff', borderRight: '1px solid #e8eaf0', display: 'flex', flexDirection: 'column', flexShrink: 0 }}>
        <div style={{ padding: '10px 12px 0', borderBottom: '1px solid #f0f1f5' }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: '#1a1f2e', marginBottom: 8 }}>Accounts</div>
          <div style={{ display: 'flex', gap: 0 }}>
            {/* GROUPS button with dropdown */}
            <div
              style={{ flex: 1, padding: '7px 8px', textAlign: 'center', fontSize: 11, cursor: 'pointer', color: accTab === 'groups' ? '#f4a118' : '#8892a4', borderBottom: `2px solid ${accTab === 'groups' ? '#f4a118' : 'transparent'}`, fontWeight: 500, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4 }}
              onClick={() => { setAccTab('groups'); setShowGroupDropdown(true); }}
            >
              ◎ Groups ›
            </div>
            <div
              style={{ flex: 1, padding: '7px 4px', textAlign: 'center', fontSize: 11, cursor: 'pointer', color: accTab === 'accounts' ? '#f4a118' : '#8892a4', borderBottom: `2px solid ${accTab === 'accounts' ? '#f4a118' : 'transparent'}`, fontWeight: 500 }}
              onClick={() => { setAccTab('accounts'); setShowGroupDropdown(false); }}
            >
              ⊟ Clients ›
            </div>
          </div>
        </div>

        {/* Account search */}
        <div style={{ padding: '7px 10px', borderBottom: '1px solid #f0f1f5', display: 'flex', alignItems: 'center', gap: 5 }}>
          <span style={{ color: '#aaa', fontSize: 13 }}>🔍</span>
          <input style={{ border: '1px solid #e2e5ed', borderRadius: 5, padding: '4px 7px', fontSize: 11, width: '100%', outline: 'none' }} placeholder="Search accounts…" value={accSearch} onChange={e => setAccSearch(e.target.value)} />
          <span style={{ color: '#aaa', fontSize: 13, cursor: 'pointer' }} title="Filter">⚡</span>
          <span style={{ color: '#aaa', fontSize: 13, cursor: 'pointer' }}>«</span>
        </div>

        {/* Select all */}
        <div style={{ padding: '5px 12px', borderBottom: '1px solid #f0f1f5', display: 'flex', alignItems: 'center', gap: 6, fontSize: 11 }}>
          <input type="checkbox" style={{ accentColor: '#0f766e' }} checked={effectiveAccountIds.size === accounts.length && accounts.length > 0} onChange={e => selectAll(e.target.checked)} />
          <span style={{ fontWeight: 500 }}>
            {effectiveAccountIds.size > 0 ? `${effectiveAccountIds.size} Accounts selected.` : 'Select All'}
          </span>
          {effectiveAccountIds.size > 0 && (
            <span style={{ color: '#0f766e', cursor: 'pointer', marginLeft: 'auto' }} onClick={() => { setSelectedAccounts([]); setSelectedGroupIds([]); setExcludedAccountIds([]); }}>Clear</span>
          )}
        </div>

        {/* Account list */}
        <div style={{ flex: 1, overflowY: 'auto' }}>
          {filteredAccounts.map(acc => {
            const p = getPlatform(acc.platform);
            const viaGroup = effectiveAccountIds.has(acc.id) && groupSelectedAccountIds.has(acc.id) && !selectedAccounts.includes(acc.id);
            return (
              <div key={acc.id} style={{ display: 'flex', alignItems: 'center', gap: 7, padding: '6px 12px', cursor: 'pointer', borderBottom: '1px solid #f5f6fa', background: viaGroup ? '#f7fafe' : 'transparent' }}
                onClick={() => toggleAccount(acc.id)} title={viaGroup ? 'Selected via group — click to uncheck just this account' : undefined}>
                <input type="checkbox" style={{ accentColor: '#0f766e', flexShrink: 0 }} checked={effectiveAccountIds.has(acc.id)} onChange={() => {}} />
                <div className="platform-icon" style={{ background: p.bg, color: p.color, width: 20, height: 20 }}>{p.icon}</div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 11, color: acc.is_connected ? '#1a1f2e' : '#e55b2d', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{acc.account_name}</div>
                  {acc.page_name && <div style={{ fontSize: 9, color: '#aaa', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{acc.page_name}</div>}
                </div>
                {!acc.is_connected && <span style={{ color: '#e55b2d', fontSize: 12 }}>↺</span>}
              </div>
            );
          })}
          {filteredAccounts.length === 0 && <div style={{ padding: 20, textAlign: 'center', color: '#aaa', fontSize: 11 }}>No accounts found</div>}
        </div>

        {/* Groups Dropdown */}
        {showGroupDropdown && (
          <div ref={groupDropRef} style={{ position: 'fixed', left: 200, top: 100, zIndex: 600, width: 280, background: '#fff', border: '1px solid #e2e5ed', borderRadius: 8, boxShadow: '0 8px 24px rgba(0,0,0,0.14)', overflow: 'hidden' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '10px 12px', borderBottom: '1px solid #f0f1f5' }}>
              <span style={{ color: '#aaa', fontSize: 13 }}>🔍</span>
              <input style={{ flex: 1, border: 'none', outline: 'none', fontSize: 12 }} placeholder="Search a group" value={groupSearch} onChange={e => setGroupSearch(e.target.value)} autoFocus />
              <span style={{ cursor: 'pointer', color: '#aaa', fontSize: 16 }} onClick={() => setShowGroupDropdown(false)}>✕</span>
            </div>
            <div style={{ maxHeight: 320, overflowY: 'auto' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '9px 12px', cursor: 'pointer', borderBottom: '1px solid #f5f6fa', fontSize: 12, color: '#1a1f2e' }}
                onClick={() => { const ids = groups.map(g => g.id); setSelectedGroupIds(ids); ids.forEach(ensureGroupAccounts); }}>
                <input type="checkbox" style={{ accentColor: '#0f766e' }} checked={selectedGroupIds.length === groups.length && groups.length > 0} readOnly /> Select All
              </div>
              {filteredGroups.map(g => (
                <div key={g.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '9px 12px', cursor: 'pointer', borderBottom: '1px solid #f5f6fa', fontSize: 12, color: '#1a1f2e' }}
                  onClick={() => toggleGroupSelection(g.id)}>
                  <input type="checkbox" style={{ accentColor: '#0f766e' }} checked={selectedGroupIds.includes(g.id)} onChange={() => {}} />
                  {g.name}
                  <span style={{ marginLeft: 'auto', fontSize: 10, color: '#8892a4' }}>{g.account_count || 0} acc.</span>
                </div>
              ))}
              {filteredGroups.length === 0 && <div style={{ padding: 20, textAlign: 'center', color: '#aaa', fontSize: 11 }}>No groups found</div>}
            </div>
            <div style={{ padding: '8px 12px', borderTop: '1px solid #f0f1f5', display: 'flex', justifyContent: 'flex-end' }}>
              <button className="btn btn-primary" style={{ fontSize: 11, padding: '5px 12px' }} onClick={() => setShowGroupDropdown(false)}>
                Done ({selectedGroupIds.length} selected)
              </button>
            </div>
          </div>
        )}
      </div>

      {/* === COMPOSER MIDDLE === */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', background: '#f5f6fa', overflow: 'hidden' }}>
        {/* Composer topbar */}
        <div style={{ height: 46, background: '#fff', borderBottom: '1px solid #e8eaf0', display: 'flex', alignItems: 'center', padding: '0 14px', gap: 8, flexShrink: 0 }}>
          <span style={{ fontSize: 13, fontWeight: 600, color: '#1a1f2e' }}>{isEdit ? 'Edit Post' : 'Create Post'}</span>
          <span style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 11, color: '#8892a4' }}>
            ☁ {autosave}
          </span>
          <div style={{ flex: 1 }} />
          <button className="btn btn-outline" style={{ fontSize: 11, padding: '4px 10px' }} onClick={() => navigate('/posts/bulk-import')}>⬆ Import</button>
          <button className="btn btn-ghost" style={{ fontSize: 11, padding: '4px 8px' }} onClick={() => navigate(-1)}>✕</button>
        </div>

        <div style={{ flex: 1, overflowY: 'auto', padding: 14 }}>
          {/* Media section */}
          <div className="card" style={{ marginBottom: 12 }}>
            <div style={{ padding: '12px 14px', borderBottom: '1px solid #f0f1f5', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <span style={{ fontSize: 11, fontWeight: 600, color: '#1a1f2e', textTransform: 'uppercase', letterSpacing: '.04em' }}>Media</span>
              {mediaFiles.length > 0 && (
                <span style={{ fontSize: 11, color: '#e55b2d', cursor: 'pointer', padding: '2px 6px', border: '1px solid #fecaca', borderRadius: 4 }}
                  onClick={() => { setMediaFiles([]); setVideoMeta({}); setVideoDurations({}); setActiveVideoId(null); }}>
                  🗑 Remove all
                </span>
              )}
            </div>
            <div style={{ padding: 14 }}>

              {/* Image thumbnails row */}
              {mediaFiles.filter(f => f.file_type?.startsWith('image/')).length > 0 && (
                <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
                  {mediaFiles.filter(f => f.file_type?.startsWith('image/')).map(f => (
                    <div key={f.id} style={{ position: 'relative' }}>
                      <img
                        src={f.file_url}
                        alt={f.original_name}
                        style={{ width: 80, height: 80, objectFit: 'cover', borderRadius: 6, border: '1px solid #e8eaf0', display: 'block', background: '#eef2f7' }}
                        onError={e => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }}
                      />
                      <div style={{ width: 80, height: 80, background: '#eef2f7', borderRadius: 6, alignItems: 'center', justifyContent: 'center', fontSize: 22, color: '#c0c7d8', border: '1px solid #e8eaf0', display: 'none' }}>🖼</div>
                      <span style={{ position: 'absolute', top: -5, right: -5, background: '#e55b2d', color: '#fff', borderRadius: '50%', width: 16, height: 16, fontSize: 9, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', fontWeight: 700 }}
                        onClick={() => removeMedia(f.id)}>✕</span>
                    </div>
                  ))}
                </div>
              )}

              {/* Video card with metadata panel */}
              {mediaFiles.filter(f => f.file_type?.startsWith('video/')).map(f => {
                const meta = videoMeta[f.id] || { title: '', thumbnailUrl: null, subtitleName: '' };
                const duration = videoDurations[f.id];
                return (
                  <div key={f.id} style={{ border: '1px solid #e8eaf0', borderRadius: 8, marginBottom: 12, overflow: 'hidden', background: '#fafbfd' }}>
                    <div style={{ display: 'flex', gap: 14, padding: 14 }}>
                      {/* Video thumbnail */}
                      <div style={{ position: 'relative', flexShrink: 0, width: 140, height: 100, borderRadius: 6, overflow: 'hidden', background: '#0d0d0d' }}>
                        <video
                          src={f.file_url}
                          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                          preload="metadata"
                          onLoadedMetadata={e => setVideoDurations(prev => ({ ...prev, [f.id]: e.target.duration }))}
                          poster={meta.thumbnailUrl || undefined}
                        />
                        {/* Play overlay */}
                        <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.25)', pointerEvents: 'none' }}>
                          <div style={{ width: 30, height: 30, borderRadius: '50%', background: 'rgba(255,255,255,0.85)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, paddingLeft: 2 }}>▶</div>
                        </div>
                        {/* Duration badge */}
                        {duration != null && (
                          <div style={{ position: 'absolute', bottom: 5, right: 5, background: 'rgba(0,0,0,0.7)', color: '#fff', fontSize: 10, fontWeight: 600, padding: '2px 5px', borderRadius: 3 }}>
                            {formatDuration(duration)}
                          </div>
                        )}
                      </div>

                      {/* Video metadata */}
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                          <span style={{ fontSize: 11, fontWeight: 600, color: '#1a1f2e' }}>Title</span>
                          <span style={{ fontSize: 10, color: '#e55b2d', cursor: 'pointer' }} onClick={() => removeMedia(f.id)}>🗑 Remove</span>
                        </div>
                        <div style={{ position: 'relative', marginBottom: 10 }}>
                          <input
                            className="form-input"
                            style={{ paddingRight: 32, fontSize: 12 }}
                            placeholder="Enter video title…"
                            value={meta.title}
                            onChange={e => setVideoMeta(prev => ({ ...prev, [f.id]: { ...prev[f.id], title: e.target.value } }))}
                          />
                          <span style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', fontSize: 16, cursor: 'pointer', title: 'Generate with AI' }}
                            title="Generate title with AI">✦</span>
                        </div>
                        <div style={{ display: 'flex', gap: 8 }}>
                          <button
                            className="btn btn-outline"
                            style={{ fontSize: 11, padding: '5px 11px', flex: 1, justifyContent: 'center' }}
                            onClick={() => { thumbInputRef.current.dataset.videoId = f.id; thumbInputRef.current.click(); }}
                          >
                            ⊞ Change Thumbnail
                          </button>
                          <button
                            className="btn btn-outline"
                            style={{ fontSize: 11, padding: '5px 11px', flex: 1, justifyContent: 'center' }}
                            onClick={() => { subInputRef.current.dataset.videoId = f.id; subInputRef.current.click(); }}
                          >
                            CC {meta.subtitleName ? '✓' : 'Add Subtitle'}
                          </button>
                        </div>
                        {meta.subtitleName && (
                          <div style={{ fontSize: 10, color: '#22c55e', marginTop: 5 }}>✓ {meta.subtitleName}</div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}

              {/* Upload drop zone */}
              <div
                style={{ border: '2px dashed #d1d8e8', borderRadius: 7, padding: mediaFiles.length ? '14px' : '28px', textAlign: 'center', cursor: 'pointer', color: '#8892a4', background: '#fafbff' }}
                onClick={() => fileInputRef.current.click()}
                onDragOver={e => e.preventDefault()}
                onDrop={e => { e.preventDefault(); handleFileUpload(e.dataTransfer.files); }}
              >
                {uploading
                  ? <><div className="spinner" style={{ margin: '0 auto 6px' }} />Uploading…</>
                  : <><div style={{ fontSize: mediaFiles.length ? 18 : 26, marginBottom: 4 }}>☁</div>
                    {mediaFiles.length ? <span style={{ fontSize: 11 }}>Add more files</span> : <><span>Drag and drop or </span><span style={{ color: '#0f766e', fontWeight: 500 }}>Upload a file</span></>}
                    <div style={{ fontSize: 10, color: '#bbb', marginTop: 3 }}>PNG, JPG, GIF, WEBP, MP4, MOV, WEBM and PDF</div>
                  </>
                }
              </div>

              <input ref={fileInputRef} type="file" multiple accept="image/*,video/*,.pdf" style={{ display: 'none' }} onChange={e => handleFileUpload(e.target.files)} />
              <input ref={thumbInputRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={e => handleThumbnailChange(e.target.dataset.videoId, e)} />
              <input ref={subInputRef} type="file" accept=".srt,.vtt,.ass" style={{ display: 'none' }} onChange={e => handleSubtitleChange(e.target.dataset.videoId, e)} />

              <div style={{ display: 'flex', gap: 8, paddingTop: 8 }}>
                <span style={{ fontSize: 12, color: '#0f766e', cursor: 'pointer', padding: '4px 6px', borderRadius: 4 }} onClick={openGallery}>🖼 Gallery</span>
              </div>
            </div>
          </div>

          {/* Caption section */}
          <div className="card">
            <div style={{ padding: '10px 14px' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                <span style={{ fontSize: 11, fontWeight: 600, color: '#1a1f2e', textTransform: 'uppercase', letterSpacing: '.04em' }}>Caption</span>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, color: '#8892a4' }}>
                  Customize
                  <div className={`toggle ${customizeOn ? 'on' : ''}`} onClick={() => setCustomizeOn(!customizeOn)} />
                </div>
              </div>

              {/* Per-platform tabs when Customize is on */}
              {customizeOn && selectedPlatforms.length > 0 && (
                <div style={{ display: 'flex', gap: 4, marginBottom: 8 }}>
                  <div style={{ padding: '5px 8px', borderRadius: 5, background: !activeCustomTab ? '#0f766e' : '#f0f1f5', color: !activeCustomTab ? '#fff' : '#555', fontSize: 10, fontWeight: 600, cursor: 'pointer' }} onClick={() => setActiveCustomTab(null)}>All</div>
                  {selectedPlatforms.map(plt => {
                    const p = getPlatform(plt);
                    const text = customCaptions[plt] ?? caption;
                    const limit = PLATFORM_CHAR_LIMITS[plt];
                    const over = limit != null && text.length > limit;
                    return (
                      <div key={plt} style={{ position: 'relative', width: 26, height: 26, borderRadius: 5, background: p.bg, color: p.color, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 9, fontWeight: 700, cursor: 'pointer', border: activeCustomTab === plt ? `2px solid ${p.color}` : '2px solid transparent' }}
                        onClick={() => setActiveCustomTab(plt)} title={over ? `Over the ${p.name} character limit` : p.name}>
                        {p.icon}
                        {over && <span style={{ position: 'absolute', top: -3, right: -3, width: 9, height: 9, borderRadius: '50%', background: '#dc2626', border: '1px solid #fff' }} />}
                      </div>
                    );
                  })}
                </div>
              )}

              <textarea
                ref={captionRef}
                className="form-textarea"
                rows={5}
                placeholder="Write your caption, then customize it for each social network"
                value={customizeOn && activeCustomTab ? (customCaptions[activeCustomTab] ?? caption) : caption}
                onChange={e => {
                  if (customizeOn && activeCustomTab) setCustomCaptions(prev => ({ ...prev, [activeCustomTab]: e.target.value }));
                  else setCaption(e.target.value);
                }}
              />
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', paddingTop: 6 }}>
                <div style={{ display: 'flex', gap: 2, alignItems: 'center', position: 'relative' }}>
                  <span style={{ fontSize: 14, cursor: 'pointer', padding: '3px 4px', borderRadius: 4 }} title="Emoji" onClick={() => setShowEmoji(v => !v)}>😊</span>
                  <span style={{ fontSize: 14, color: '#666', cursor: 'pointer', padding: '3px 4px', borderRadius: 4 }} title="Mention" onClick={() => insertAtCursor('@')}>@</span>
                  <span style={{ fontSize: 14, color: '#666', cursor: 'pointer', padding: '3px 4px', borderRadius: 4 }} title="Hashtag" onClick={() => insertAtCursor('#')}>#</span>
                  {showEmoji && (
                    <div style={{ position: 'absolute', bottom: 26, left: 0, background: '#fff', border: '1px solid #e2e5ed', borderRadius: 8, padding: 6, display: 'grid', gridTemplateColumns: 'repeat(8,1fr)', gap: 2, width: 210, boxShadow: '0 4px 14px rgba(15,20,40,0.12)', zIndex: 20 }}>
                      {EMOJIS.map(e => (
                        <span key={e} style={{ fontSize: 16, cursor: 'pointer', textAlign: 'center', padding: 2 }}
                          onClick={() => { insertAtCursor(e); setShowEmoji(false); }}>{e}</span>
                      ))}
                    </div>
                  )}
                </div>
                <span style={{ fontSize: 11, color: counterColor, fontWeight: tightestLimit != null ? 700 : 400 }}>
                  {editingText.length}{tightestLimit != null ? ` / ${tightestLimit}` : ''}
                </span>
              </div>
              {isOverLimit && (
                <div style={{ fontSize: 10, color: '#dc2626', marginTop: 4 }}>
                  ⚠ {customizeOn && activeCustomTab
                    ? `Over the ${getPlatform(activeCustomTab).name} limit by ${editingText.length - tightestLimit} characters.`
                    : `Exceeds the ${getPlatform(tightestPlatformId).name} limit (${tightestLimit} chars) by ${editingText.length - tightestLimit}. Turn on Customize and edit the ${getPlatform(tightestPlatformId).name} tab to shorten it for that platform.`}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* LinkedIn Page warning */}
        {hasLinkedInPage && (
          <div style={{ background: '#f0fdfa', borderTop: '1px solid #99f6e4', padding: '7px 14px', fontSize: 11, color: '#134e4a', display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
            <span style={{ fontSize: 14 }}>ℹ️</span>
            <span><strong>LinkedIn Pages require Marketing Developer Platform approval</strong> to publish. Posts to pages will fail until approved. <a href="https://www.linkedin.com/developers/apps" target="_blank" rel="noreferrer" style={{ color: '#0f766e' }}>Apply at linkedin.com/developers</a></span>
          </div>
        )}

        {/* YouTube no-video warning */}
        {hasYouTubeSelected && !hasVideo && (
          <div style={{ background: '#fffbeb', borderTop: '1px solid #fde68a', padding: '7px 14px', fontSize: 11, color: '#92400e', display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
            <span style={{ fontSize: 14 }}>⚠️</span>
            <span><strong>YouTube requires a video.</strong> Please upload an MP4 or MOV file before publishing.</span>
          </div>
        )}

        {/* Google Business Profile doesn't support video */}
        {gmbVideoWarning && (
          <div style={{ background: '#fffbeb', borderTop: '1px solid #fde68a', padding: '7px 14px', fontSize: 11, color: '#92400e', display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
            <span style={{ fontSize: 14 }}>⚠️</span>
            <span><strong>Google Business Profile doesn't support video.</strong> {hasImage ? 'Only the attached image will be published to it — the video will be skipped.' : 'The video will be skipped for this platform, so it will publish with no media.'}</span>
          </div>
        )}

        {/* Footer */}
        <div style={{ height: 52, background: '#fff', borderTop: '1px solid #e8eaf0', display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 7, padding: '0 14px', flexShrink: 0 }}>
          <button className="btn btn-outline" onClick={saveDraft} disabled={publishing}>
            📄 {isEdit ? 'Save Draft' : 'Save Draft'}
          </button>
          <button className="btn btn-outline" onClick={openScheduleModal} disabled={publishing}>
            📅 {isEdit ? 'Reschedule' : 'Schedule Post'}
          </button>
          <button
            className="btn btn-primary"
            onClick={publishNow}
            disabled={publishing}
            style={{ minWidth: 130, justifyContent: 'center' }}
          >
            {publishing
              ? <><span className="spinner" style={{ width: 12, height: 12, borderWidth: 2, marginRight: 6 }} />Publishing…</>
              : '⚡ Publish Now'}
          </button>
        </div>
      </div>

      {/* === PREVIEW PANEL === */}
      <div style={{ width: 340, background: '#fff', borderLeft: '1px solid #e8eaf0', display: 'flex', flexDirection: 'column', flexShrink: 0 }}>
        <div style={{ padding: '12px 13px', borderBottom: '1px solid #e8eaf0', fontSize: 12, fontWeight: 600, color: '#1a1f2e' }}>Preview ⓘ</div>
        <div style={{ flex: 1, overflowY: 'auto', padding: 10 }}>
          {effectiveAccountIds.size === 0 ? (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#aaa', fontSize: 11, textAlign: 'center', gap: 8 }}>
              <span style={{ fontSize: 28 }}>👁</span>
              Select accounts to see the preview
            </div>
          ) : (
            accounts.filter(a => effectiveAccountIds.has(a.id)).map(acc => {
              const p = getPlatform(acc.platform);
              const c = customizeOn ? (customCaptions[acc.platform] ?? caption) : caption;
              const limit = PLATFORM_CHAR_LIMITS[acc.platform];
              const overLimit = limit != null && c.length > limit;
              return (
                <div key={acc.id} style={{ border: '1px solid #e8eaf0', borderRadius: 7, marginBottom: 10, overflow: 'hidden' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 7, padding: '7px 9px', borderBottom: '1px solid #f0f1f5' }}>
                    <div className="platform-icon" style={{ background: p.bg, color: p.color, width: 22, height: 22 }}>{p.icon}</div>
                    <div>
                      <div style={{ fontSize: 11, fontWeight: 600, color: '#1a1f2e' }}>{acc.account_name.slice(0, 28)}</div>
                      <div style={{ fontSize: 9, color: '#aaa' }}>Now</div>
                    </div>
                  </div>
                  {/* Media preview in post card */}
                  {mediaFiles.length > 0 && (() => {
                    const vid = mediaFiles.find(f => f.file_type?.startsWith('video/'));
                    const imgs = mediaFiles.filter(f => f.file_type?.startsWith('image/'));
                    if (vid) return (
                      <div style={{ position: 'relative', background: '#000', height: 100 }}>
                        <video src={vid.file_url} style={{ width: '100%', height: '100%', objectFit: 'cover', opacity: 0.85 }} poster={videoMeta[vid.id]?.thumbnailUrl || undefined} />
                        <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          <div style={{ width: 22, height: 22, borderRadius: '50%', background: 'rgba(255,255,255,0.8)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 9, paddingLeft: 1 }}>▶</div>
                        </div>
                      </div>
                    );
                    if (imgs.length === 1) return (
                      <img src={imgs[0].file_url} alt="" style={{ width: '100%', height: 100, objectFit: 'cover', display: 'block' }} />
                    );
                    if (imgs.length > 1) return (
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1, height: 100 }}>
                        {imgs.slice(0, 4).map(img => <img key={img.id} src={img.file_url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />)}
                      </div>
                    );
                    return null;
                  })()}
                  <div style={{ padding: '8px 9px', fontSize: 11, color: overLimit ? '#dc2626' : '#333', lineHeight: 1.5, minHeight: 40, background: overLimit ? '#fef2f2' : 'transparent' }}>
                    {c || <span style={{ color: '#ccc' }}>Caption will appear here…</span>}
                  </div>
                  {overLimit && (
                    <div style={{ padding: '0 9px 8px', fontSize: 10, color: '#dc2626', fontWeight: 600 }}>
                      ⚠ {c.length}/{limit} — {c.length - limit} over {p.name}'s limit. Use Customize to trim it.
                    </div>
                  )}
                  {(() => {
                    const actions = getPreviewActions(acc.platform);
                    if (actions.style === 'share-only') {
                      return (
                        <div style={{ display: 'flex', justifyContent: 'flex-end', padding: '8px 9px', borderTop: '1px solid #f0f1f5' }}>
                          <actions.Icon size={15} color="#8892a4" />
                        </div>
                      );
                    }
                    if (actions.style === 'labeled') {
                      return (
                        <div style={{ display: 'flex', justifyContent: 'space-around', padding: '8px 4px', borderTop: '1px solid #f0f1f5' }}>
                          {actions.items.map(it => (
                            <span key={it.label} style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 10, color: '#65676b', fontWeight: 500 }}>
                              <it.Icon size={14} color="#65676b" />{it.label}
                            </span>
                          ))}
                        </div>
                      );
                    }
                    return (
                      <div style={{ display: 'flex', justifyContent: 'space-around', alignItems: 'center', padding: '8px 6px', borderTop: '1px solid #f0f1f5' }}>
                        {actions.items.map((ItemIcon, i) => <ItemIcon key={i} size={15} color="#8892a4" />)}
                        {actions.trailing && <actions.trailing size={15} color="#8892a4" style={{ marginLeft: 'auto' }} />}
                      </div>
                    );
                  })()}
                </div>
              );
            })
          )}
        </div>
      </div>

      {/* === SCHEDULE MODAL === */}
      {showGallery && (
        <div className="modal-overlay" onClick={() => setShowGallery(false)}>
          <div className="modal" style={{ width: 520 }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <span className="modal-title">Media Library</span>
              <button className="btn btn-ghost" style={{ fontSize: 12 }} onClick={() => setShowGallery(false)}>✕</button>
            </div>
            <div className="modal-body" style={{ maxHeight: 380, overflowY: 'auto' }}>
              {library === null && <div className="spinner" style={{ margin: '20px auto' }} />}
              {library && !library.length && (
                <div style={{ textAlign: 'center', color: '#aaa', fontSize: 12, padding: 20 }}>No uploads yet</div>
              )}
              {library && library.length > 0 && (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 8 }}>
                  {library.map(f => (
                    <div key={f.id} title={f.original_name} style={{ cursor: 'pointer', border: '1px solid #e8eaf0', borderRadius: 6, overflow: 'hidden', background: '#f7f8fb' }}
                      onClick={() => addFromGallery(f)}>
                      {f.file_type?.startsWith('image/')
                        ? <img src={f.file_url} alt={f.original_name} style={{ width: '100%', height: 90, objectFit: 'cover', display: 'block' }} />
                        : <div style={{ height: 90, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22, color: '#c0c7d8' }}>{f.file_type?.startsWith('video/') ? '🎬' : '📄'}</div>}
                      <div style={{ fontSize: 9, color: '#8892a4', padding: '3px 4px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{f.original_name}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {showScheduleModal && (
        <div className="modal-overlay" onClick={() => setShowScheduleModal(false)}>
          <div className="modal" style={{ width: 360 }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <span className="modal-title">Schedule Post</span>
              <span style={{ cursor: 'pointer', color: '#888' }} onClick={() => setShowScheduleModal(false)}>✕</span>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label className="form-label">Date</label>
                <input className="form-input" type="date" value={scheduledAt} onChange={e => setScheduledAt(e.target.value)} />
              </div>
              <div className="form-group" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                <div>
                  <label className="form-label">Time</label>
                  <input className="form-input" type="time" value={scheduledTime} onChange={e => setScheduledTime(e.target.value)} />
                </div>
                <div>
                  <label className="form-label">Timezone</label>
                  <select className="form-select">
                    <option>UTC+10:00 AEST</option>
                    <option>UTC+00:00 GMT</option>
                    <option>UTC-05:00 EST</option>
                  </select>
                </div>
              </div>
              <div style={{ fontSize: 11, color: '#8892a4', marginBottom: 14 }}>ⓘ Scheduled in your workspace timezone</div>
              {selectedGroupIds.length > 0 && (
                <div style={{ padding: '8px 10px', background: '#f0f7ff', borderRadius: 6, fontSize: 11, color: '#0f766e', marginBottom: 10 }}>
                  📦 Will post to {selectedGroupIds.length} group(s) + {selectedAccounts.length} individual account(s)
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button className="btn btn-outline" onClick={() => setShowScheduleModal(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={confirmSchedule}>📨 Confirm Schedule</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
